/**
 * PH Restaurant POS - Sync Status Indicator
 * Shows online/offline status and pending sync operations
 */
import React, { useState, useEffect } from 'react';
import { useOffline } from '../context/OfflineContext';
import { 
  Wifi, WifiOff, RefreshCw, AlertTriangle, CheckCircle, 
  CloudOff, Cloud, Database, Loader2 
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

export function SyncStatusIndicator({ compact = false }) {
  const { 
    isOnline, 
    isSyncing, 
    pendingCount, 
    lastSyncTime, 
    syncError,
    triggerSync,
    getDbStats,
    serviceWorkerReady
  } = useOffline();

  const [dbStats, setDbStats] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getDbStats().then(setDbStats);
    }
  }, [isOpen, getDbStats]);

  const formatLastSync = (time) => {
    if (!time) return 'Never';
    const date = new Date(time);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = () => {
    if (!isOnline) return 'text-red-600';
    if (syncError) return 'text-orange-600';
    if (pendingCount > 0) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusIcon = () => {
    if (isSyncing) {
      return <RefreshCw className="w-4 h-4 animate-spin" />;
    }
    if (!isOnline) {
      return <WifiOff className="w-4 h-4" />;
    }
    if (syncError) {
      return <AlertTriangle className="w-4 h-4" />;
    }
    if (pendingCount > 0) {
      return <CloudOff className="w-4 h-4" />;
    }
    return <Cloud className="w-4 h-4" />;
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${getStatusColor()}`} data-testid="sync-status-compact">
        {getStatusIcon()}
        {pendingCount > 0 && (
          <span className="text-xs bg-yellow-500 text-black px-1 rounded">
            {pendingCount}
          </span>
        )}
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`gap-2 ${getStatusColor()}`}
          data-testid="sync-status-btn"
        >
          {getStatusIcon()}
          <span className="text-xs hidden md:inline">
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {pendingCount > 0 && (
            <span className="text-xs bg-yellow-500 text-black px-1.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-card border-border" align="end">
        <div className="space-y-4">
          {/* Connection Status */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <Wifi className="w-5 h-5 text-green-600" />
              ) : (
                <WifiOff className="w-5 h-5 text-red-600" />
              )}
              <span className="font-medium">
                {isOnline ? 'Connected' : 'Offline Mode'}
              </span>
            </div>
            {serviceWorkerReady && (
              <span className="text-xs text-muted-foreground">SW Ready</span>
            )}
          </div>

          {/* Sync Status */}
          <div className="bg-muted rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Last Sync</span>
              <span>{formatLastSync(lastSyncTime)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Pending Operations</span>
              <span className={pendingCount > 0 ? 'text-yellow-600' : 'text-green-600'}>
                {pendingCount}
              </span>
            </div>
            {syncError && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="w-4 h-4" />
                <span className="truncate">{syncError}</span>
              </div>
            )}
          </div>

          {/* Database Stats */}
          {dbStats && (
            <div className="bg-muted rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Database className="w-4 h-4 text-secondary" />
                <span>Local Database</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>Categories:</span>
                  <span className="text-foreground">{dbStats.categories}</span>
                </div>
                <div className="flex justify-between">
                  <span>Menu Items:</span>
                  <span className="text-foreground">{dbStats.menuItems}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tables:</span>
                  <span className="text-foreground">{dbStats.tables}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tickets:</span>
                  <span className="text-foreground">{dbStats.tickets}</span>
                </div>
              </div>
              {dbStats.syncQueue && (
                <div className="border-t border-border pt-2 mt-2">
                  <div className="text-xs text-muted-foreground">
                    Sync Queue: {dbStats.syncQueue.pending} pending, {dbStats.syncQueue.failed} failed
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              onClick={() => triggerSync()} 
              disabled={!isOnline || isSyncing}
              className="flex-1 bg-secondary hover:bg-secondary/90"
              size="sm"
              data-testid="manual-sync-btn"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>

          {/* Offline Mode Notice */}
          {!isOnline && (
            <div className="bg-orange-900/30 border border-orange-700/50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <CloudOff className="w-4 h-4 text-orange-600 mt-0.5" />
                <div className="text-sm text-orange-300">
                  <p className="font-medium">Working Offline</p>
                  <p className="text-orange-600 mt-1 text-xs">
                    Your changes will sync automatically when you're back online.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Offline Mode Banner - shows at top of screen when offline
 */
export function OfflineModeBanner() {
  const { isOnline, pendingCount } = useOffline();

  if (isOnline) return null;

  return (
    <div 
      className="bg-orange-600 text-foreground px-4 py-2 flex items-center justify-center gap-2 text-sm"
      data-testid="offline-banner"
    >
      <WifiOff className="w-4 h-4" />
      <span>You're offline. Changes will sync when connected.</span>
      {pendingCount > 0 && (
        <span className="bg-orange-800 px-2 py-0.5 rounded text-xs">
          {pendingCount} pending
        </span>
      )}
    </div>
  );
}

export default SyncStatusIndicator;
